import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../../services/mdr-dashboards.service';
import { SemiCircleGaugeDataSource } from '../../../interfaces/mdr-dashboards-chart-area-data.interface';
import { DataResponse, SemiCircleGaugeResponse } from '../../../interfaces/mdr-dashboards.data-response.interface';
import { catchError, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { OnboardingIssue, OnboardingIssueDescriptions } from '../../../../admin/companies-onboarding-issues/enums/onboarding-issue.enum';
import { CompanyDetailsInterface } from '../../../../companies/interfaces/company-details.interface';
import { OrganizationData } from '../../../../organizations/interfaces/OrganizationData';
import { isEqual } from 'lodash-es';
import { IdentityService } from '../../../../auth/services/identity.service';
import { CompaniesService } from '../../../../companies/services/companies.service';

@Component({
    selector: 'app-mdr-dashboards-semi-circle-gauge',
    templateUrl: './mdr-dashboards-semi-circle-gauge.component.html',
    styleUrls: ['./mdr-dashboards-semi-circle-gauge.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class MdrDashboardsSemiCircleGaugeComponent implements OnInit, OnDestroy, OnChanges {
    /** @var startDate - start date of the chart */
    @Input() public startDate: string;

    /** @var endDate - end date of the chart */
    @Input() public endDate: string;

    /**
     * @var element - element object passed from the tab's component.
     * This object will contain specific settings and custom options for the radial bar chart widget
     */
    @Input() public element: SemiCircleGaugeDataSource[];

    /** @var bulletType - set the bullet type for the legend. By default, is square */
    @Input() public bulletType = 'square';

    /** @var strokeWidth - set the stroke width for the radial bar chart. By default, is 5 */
    @Input() public strokeWidth = 5;

    /** @var tableLegend - enable/disable the table legend style for the specific widget */
    @Input() public tableLegend = false;

    /** @var width - widget width */
    @Input() public width: number | string = 214;

    /** @var width - container width, including the table under the radial bar (if exists) */
    @Input() public containerWidth: number | string = 300;

    /** @var height - widget height */
    @Input() public height: number | string = 200;

    /** @var loading - enable/disable loading state for this specific indicator */
    @Input() public loading = false;

    /** @var fontSize - font size of the center title */
    @Input() public fontSize = '20px';

    public dashArray: number[] = [];
    public dashOffset: number[] = [];
    public value: number[] = [];
    public indicatorRotation: number[] = [];
    public markerStyles: string[] = [];
    public defaultCategoriesIterator = Array(4).fill(0);
    public loadedData: any[] = [];
    public loadTimeout: any;
    public subscriptionsCharts: Subscription[] = [];
    public organizationSubscription: Subscription;
    public organization: CompanyDetailsInterface | null;
    public loadedCompany: boolean;

    public constructor(
        public organizationService: OrganizationsService,
        public companiesService: CompaniesService,
        public identityService: IdentityService,
        public mdrDashboardsService: MdrDashboardsService,
        public cdr: ChangeDetectorRef,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.element && changes.element.currentValue !== changes.element.previousValue) {
            this.loadChartInfo();
        }
    }

    ngOnDestroy(): void {
        this.cdr.detach();
        this.loadTimeout = null;
        this.subscriptionsCharts.forEach((subscription) => {
            subscription?.unsubscribe();
        });
    }

    public ngOnInit() {
        this.dashArray = Array(this.element.length).fill(142);
        this.dashOffset = Array(this.element.length).fill(142);
        this.indicatorRotation = Array(this.element.length).fill(0);
        this.value = Array(this.element.length).fill(100);
        this.markerStyles = Array(this.element.length).fill('transform: rotate(0deg)');
        this.loadedCompany = false;
        this.organizationSubscription = this.organizationService
            .selectedOrganization$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((organization: OrganizationData) => {
                this.organization = (organization as CompanyDetailsInterface) || this.identityService.company;
                this.companiesService.getCompanyDetails(this.organization.id).subscribe((org) => {
                    this.organization = org;
                    this.loadedCompany = true;
                });
            });
    }

    public loadChartInfo() {
        this.loadedData = [];
        for (let i = 0; i < this.element.length; i++) {
            this.subscriptionsCharts[i] = this.element[i].data
                .pipe(
                    map((data: DataResponse) => ({
                        loading: false,
                        data: data.data,
                        error: null,
                    })),
                    startWith({
                        loading: true,
                        data: null,
                        error: null,
                    }),
                    catchError((error) => {
                        this.refreshDataAndClearTimeout();
                        return of({
                            loading: false,
                            error,
                            data: null,
                        });
                    }),
                )
                .subscribe((value) => {
                    this.loadedData[i] = { chart: this.element[i], response: value };
                    this.cdr.detectChanges();
                    if (document.getElementById('gradient' + i)) {
                        this.refreshDataAndClearTimeout();
                    }
                });
        }
    }

    public refreshDataAndClearTimeout() {
        this.loadTimeout = setTimeout(() => {
            this.cdr.detectChanges();
            clearTimeout(this.loadTimeout);
        }, 1500);
    }

    public processReceivedData(data: SemiCircleGaugeResponse, chartIndex: number): string {
        if (!data) {
            return '- -';
        }

        const type = data.type;
        const value = data.value;

        let finalValue = '';
        let totalOptions = 0;

        if (type === 'percent') {
            finalValue = value + '%';
        }

        if (type === 'ratio') {
            if (typeof data.data.length !== 'undefined') {
                totalOptions = data.data.length;
            }
            if (value === 0) {
                finalValue = 0 + '/' + totalOptions;
            } else {
                finalValue = (totalOptions * value) / 100 + '/' + totalOptions;
            }

            this.mdrDashboardsService.statusOfDeploymentValue = {
                currentStatus: (totalOptions * value) / 100,
                total: totalOptions,
            };
        }

        this.value[chartIndex] = value;
        this.updateProgress(this.value[chartIndex], chartIndex);
        return finalValue;
    }

    public updateProgress(value: number, chartIndex: number) {
        if (value >= 100) {
            this.dashArray[chartIndex] = 140;
            // show the progress at 100 if the value is > 100;
            this.dashOffset[chartIndex] = 0;
        } else if (value > 0) {
            this.dashOffset[chartIndex] = this.dashArray[chartIndex] - (value * this.dashArray[chartIndex]) / 100 - 1;
        } else {
            // reset the properties if the value falls back to 0
            this.dashArray[chartIndex] = 142;
            this.dashOffset[chartIndex] = 142;
        }

        this.updateGaugeAnimation(chartIndex);
    }

    public updateGaugeAnimation(chartIndex: number) {
        /** We are using 180 (a half circle angle of rotation) to calculate the
         *  marker position on the gauge.
         *  However, at a full rotation, when the value = 100, the marker falls
         *  below the gauge. In order to make the marker dot stop just above the
         *  baseline, we need to set a smaller value for the angle of rotation.
         *  In this case a value of 178 is optimal to display the dot correctly.
         */

        if (this.value[chartIndex] < 100) {
            this.indicatorRotation[chartIndex] = (this.value[chartIndex] / 100) * 180;
        } else if (this.value[chartIndex] > 100) {
            /** we need the indicator to stop at the end of the gauge if by any
             * chance the value will be greater than 100
             */
            this.indicatorRotation[chartIndex] = 178;
        } else {
            this.indicatorRotation[chartIndex] = (this.value[chartIndex] / 100) * 178;
        }
        this.markerStyles[chartIndex] = 'transform: rotate(' + this.indicatorRotation[chartIndex] + 'deg)';
    }

    public processLineColor(color: string, index: number): string {
        return index * 25 + '%';
    }

    public translateIssue(reason: string): string {
        // try to get a translation of known reason code, otherwise show the reason as-is
        return OnboardingIssueDescriptions[reason as OnboardingIssue] ?? reason;
    }
}
